import type { DailyBar, LatestDataFile, MarketSnapshot } from "./types";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export function validateDailyBar(bar: DailyBar): string[] {
  const errors: string[] = [];
  const values: Array<[keyof DailyBar, unknown]> = [
    ["previousClose", bar.previousClose],
    ["open", bar.open],
    ["high", bar.high],
    ["low", bar.low],
    ["close", bar.close],
    ["change", bar.change],
    ["changeRate", bar.changeRate]
  ];

  if (!DATE_RE.test(bar.date)) errors.push("date must be YYYY-MM-DD");
  for (const [key, value] of values) {
    if (!isFiniteNumber(value)) errors.push(`${String(key)} must be finite`);
  }
  if (errors.length > 0) return errors;
  if (bar.previousClose <= 0) errors.push("previousClose must be positive");
  if (bar.close <= 0) errors.push("close must be positive");
  if (bar.high < bar.low) errors.push("high must be greater than or equal to low");
  if (bar.high < bar.open) errors.push("high must be greater than or equal to open");
  if (bar.high < bar.close) errors.push("high must be greater than or equal to close");
  if (bar.low > bar.open) errors.push("low must be less than or equal to open");
  if (bar.low > bar.close) errors.push("low must be less than or equal to close");

  const expectedChange = bar.close - bar.previousClose;
  if (Math.abs(expectedChange - bar.change) > 0.02) errors.push("change mismatch");
  const expectedRate = (expectedChange / bar.previousClose) * 100;
  if (Math.abs(expectedRate - bar.changeRate) > 0.02) errors.push("changeRate mismatch");
  if (bar.volume !== undefined && (!Number.isFinite(bar.volume) || bar.volume < 0)) {
    errors.push("volume must be a non-negative finite number");
  }
  return errors;
}

export function validateSnapshot(snapshot: MarketSnapshot): string[] {
  const errors = validateDailyBar(snapshot);
  if (!snapshot.symbolId) errors.push("symbolId required");
  if (!snapshot.source) errors.push("source required");
  if (!snapshot.dataAsOf) errors.push("dataAsOf required");
  if (!snapshot.retrievedAt) errors.push("retrievedAt required");
  if (!["available", "not_ready", "unavailable", "stale"].includes(snapshot.status)) {
    errors.push("invalid status");
  }
  return errors;
}

export function validateLatestFile(file: LatestDataFile): string[] {
  const errors: string[] = [];
  if (file.schemaVersion !== 1) errors.push("schemaVersion must be 1");
  if (!file.generatedAt) errors.push("generatedAt required");
  if (!Array.isArray(file.symbols)) errors.push("symbols must be an array");
  for (const symbol of file.symbols ?? []) {
    errors.push(...validateSnapshot(symbol).map((error) => `${symbol.symbolId}: ${error}`));
  }
  return errors;
}

export function assertNoSecretText(serialized: string): void {
  const suspicious = [/ALPHA_VANTAGE_API_KEY/i, /google-oauth/i, /client_secret/i, /private_key/i];
  const hit = suspicious.find((pattern) => pattern.test(serialized));
  if (hit) throw new Error(`Output contains suspicious secret marker: ${hit.source}`);
}
